import { cx } from "./primitives";
import { MASK } from "../../lib/replayMask";

const SIZE = {
  sm: "h-7 w-7 text-xs",
  md: "h-9 w-9 text-sm",
  lg: "h-12 w-12 text-xl",
} as const;

/**
 * The face beside a name: their profile picture when they set one (2026-09-13), else
 * the initials disc — a person's first letter on the accent ground. The profile's
 * header wore this inline; the Feed puts one on every post, comment and board row, and
 * the menu's Profile row wears it too, so it is a component and the surfaces cannot
 * drift. The picture is a 256px square the server cut, so `object-cover` never crops
 * anything the server did not already.
 */
/** A hue from a name, the same every time — so the same person is the same colour. */
function hueOf(name: string): number {
  let h = 0;
  for (const ch of name.toLowerCase()) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return h % 360;
}

export function Avatar({
  name,
  src,
  size = "md",
  className,
  distinct,
}: {
  name: string;
  src?: string;
  size?: keyof typeof SIZE;
  className?: string;
  /**
   * The initials disc in a hue of the person's own instead of the accent. OPT-IN, for
   * the one place faces sit overlapping (a room's roster pill, 2026-09-19): three "R"s on
   * the same yellow read as one blob there, while everywhere else — a post, a comment, a
   * board row — the face stands beside its name and the accent is the brand's.
   */
  distinct?: boolean;
}) {
  if (src) {
    return (
      <img
        src={src}
        alt=""
        aria-hidden
        draggable={false}
        className={cx("shrink-0 rounded-full bg-[var(--color-panel-2)] object-cover", SIZE[size], className)}
      />
    );
  }
  return (
    // The initial is the first letter of a username: masked in session replay.
    <div
      aria-hidden
      {...MASK}
      className={cx(
        "flex shrink-0 items-center justify-center rounded-full font-bold uppercase text-white",
        !distinct && "bg-[var(--color-accent)]",
        SIZE[size],
        className,
      )}
      style={distinct ? { backgroundColor: `hsl(${hueOf(name)} 55% 42%)` } : undefined}
    >
      {name.slice(0, 1)}
    </div>
  );
}
